import { useEffect, useState } from 'react';
import api, { BROWSER_SESSION_EXPIRED_EVENT, BROWSER_SESSION_ROTATED_EVENT } from '../services/api.ts';
import { getPlatformAvailabilityIdentity } from '../services/platformAvailability.ts';

type Kind = 'image' | 'audio' | 'video';
const ASSET_PATH = /^\/(?:api\/)?workflows\/media\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/content$/;
const SCOPE_KEYS = ['user_type', 'user_id', 'client_id', 'clinic_id'];
const scopeIdentity = () => {
  try { return `${getPlatformAvailabilityIdentity()}|${SCOPE_KEYS.map(key => localStorage.getItem(key) || '').join(':')}`; }
  catch { return getPlatformAvailabilityIdentity(); }
};

export function workflowMessageAsset(content: unknown): string | null {
  if (typeof content === 'string') {
    const match = ASSET_PATH.exec(content);
    if (match) return match[1];
    try { return workflowMessageAsset(JSON.parse(content)); } catch { return null; }
  }
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    const data = content as Record<string, unknown>;
    for (const key of ['mediaPath', 'url', 'imageUrl', 'audioUrl', 'videoUrl']) {
      if (typeof data[key] === 'string') {
        const match = ASSET_PATH.exec(data[key] as string);
        if (match) return match[1];
      }
    }
  }
  return null;
}

type State = { key: string; status: 'idle' | 'loading' | 'ready' | 'unavailable'; src: string | null };
export function useWorkflowMessageMedia(assetId: string, kind: Kind, messageId?: number | null) {
  const [revision, setRevision] = useState(0), [expired, setExpired] = useState(false);
  const [requestedKey, setRequested] = useState('');
  useEffect(() => {
    const changed = () => { setExpired(false); setRevision(value => value + 1); };
    const ended = () => { setExpired(true); setRevision(value => value + 1); };
    const storage = (event: StorageEvent) => { if (event.key === null || SCOPE_KEYS.includes(event.key)) changed(); };
    window.addEventListener(BROWSER_SESSION_ROTATED_EVENT, changed);
    window.addEventListener(BROWSER_SESSION_EXPIRED_EVENT, ended);
    window.addEventListener('clinicChanged', changed);
    window.addEventListener('userPermissionsChanged', changed);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener(BROWSER_SESSION_ROTATED_EVENT, changed);
      window.removeEventListener(BROWSER_SESSION_EXPIRED_EVENT, ended);
      window.removeEventListener('clinicChanged', changed);
      window.removeEventListener('userPermissionsChanged', changed);
      window.removeEventListener('storage', storage);
    };
  }, []);
  const scope = scopeIdentity(), key = `${scope}|${revision}|${messageId ?? ''}|${assetId}|${kind}|${expired}`;
  const valid = Number.isSafeInteger(messageId) && Number(messageId) > 0 && ASSET_PATH.test(`/workflows/media/${assetId}/content`) && !expired;
  const initial: State['status'] = !valid ? 'unavailable' : kind === 'image' || requestedKey === key ? 'loading' : 'idle';
  const [state, setState] = useState<State>({ key, status: initial, src: null });
  useEffect(() => {
    let cancelled = false, objectUrl: string | null = null;
    const controller = new AbortController();
    setState({ key, status: initial, src: null });
    if (initial !== 'loading') return () => controller.abort();
    // The backend resolves the asset from the authorized message receipt.
    // The catalog endpoint and supplied content URL are never used for chat access.
    void api.get(`/api/chat-optimized/messages/${messageId}/workflow-media`, {
      responseType: 'blob', timeout: 20000, signal: controller.signal,
    }).then(response => {
      if (cancelled || scopeIdentity() !== scope) return;
      const blob = response.data;
      if (!(blob instanceof Blob) || !blob.size || blob.size > 16 * 1024 * 1024) throw new Error('Mídia indisponível');
      const mime = (blob.type || response.headers?.['content-type'] || '').split(';')[0].toLowerCase();
      const supported = kind === 'image' ? /^image\/(jpeg|png)$/ : kind === 'audio' ? /^audio\/(aac|mp4|mpeg|amr|ogg)$/ : /^video\/(mp4|3gpp)$/;
      if (!supported.test(mime)) throw new Error('Mídia indisponível');
      objectUrl = URL.createObjectURL(blob);
      setState({ key, status: 'ready', src: objectUrl });
    }).catch(() => { if (!cancelled && scopeIdentity() === scope) setState({ key, status: 'unavailable', src: null }); });
    return () => { cancelled = true; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [initial, key, kind, messageId, scope]);
  return { ...(state.key === key ? state : { key, status: initial, src: null }), prepare: () => setRequested(key) };
}
